import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Trash2, Save, Plus, MapPin, ArrowRight, X, ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine, Search, Warehouse, Map, Package } from "lucide-react";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { IconTextButton, IconButton, TextButton } from "../../../components/ButtonComponents";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { useProductStore } from "../../../store/productStore";
import { useWarehouseStore } from "../../../store/warehouse";
import { useWarehouseLocationStore } from "../../../store/warehouse";
import { toast } from "react-toastify";
import { Button } from "primereact/button";

// Yeni yazdığımız modalı import ediyoruz
import { RollTransactionModal } from "../../../hooks/RollTransactionModal";

interface WarehouseMovementDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    initialData?: any;
}

const MOVEMENT_TYPES = [
    { label: 'Depo Girişi (Sarf)', value: 'IN' },
    { label: 'Depo Çıkışı (Fire)', value: 'OUT' },
    { label: 'Depolar Arası Transfer', value: 'TRANSFER' },
];

export const WarehouseMovementDialog: React.FC<WarehouseMovementDialogProps> = ({ isOpen, onClose, onSave, initialData }) => {
    const { fetchDataDynamic: fetchStocks } = useProductStore();
    const { fetchDataDynamic: fetchWarehouses } = useWarehouseStore();
    const { fetchDataDynamic: fetchLocations } = useWarehouseLocationStore();

    const stockSelect = useSelectDialog<any>();
    const warehouseSelect = useSelectDialog<any>();
    const locationSelect = useSelectDialog<any>();

    // --- Modal State'leri ---
    const [isRollModalOpen, setIsRollModalOpen] = useState(false);
    const [selectedItemForRolls, setSelectedItemForRolls] = useState<any>(null);

    const [formData, setFormData] = useState({
        type: 'IN', // IN, OUT, TRANSFER
        sourceWarehouseId: null as number | null,
        sourceWarehouseName: "",
        locationId: null as number | null, // 🌟 YENİ: Kaynak Raf/Konum
        locationName: "",
        targetWarehouseId: null as number | null,
        targetWarehouseName: "",
        targetLocationId: null as number | null, // 🌟 YENİ: Hedef Raf/Konum
        targetLocationName: "",
        reference: "",
        description: "",
        date: new Date(),
        items: [] as any[]
    });

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                const movementType = initialData.type?.toUpperCase() || 'IN';

                setFormData({
                    type: movementType,
                    sourceWarehouseId: initialData.warehouseId,
                    sourceWarehouseName: initialData.warehouse?.name || "Bilinmeyen Depo",
                    locationId: initialData.locationId || null,
                    locationName: initialData.location?.name || "",
                    targetWarehouseId: initialData.targetWarehouseId || null,
                    targetWarehouseName: initialData.targetWarehouseName || "",
                    targetLocationId: initialData.targetLocationId || null,
                    targetLocationName: initialData.targetLocation?.name || "",
                    reference: initialData.reference || "",
                    description: initialData.description || "",
                    date: new Date(initialData.createdAt),
                    items: [{
                        id: initialData.id,
                        stockId: initialData.stockId,
                        variantId: initialData.variantId,
                        stockName: initialData.stock?.name || "Bilinmeyen Ürün",
                        quantity: Math.abs(initialData.quantity),
                        rollTransactions: initialData.rollTransactions || []
                    }]
                });
            } else {
                setFormData({
                    type: 'IN', sourceWarehouseId: null, sourceWarehouseName: "", locationId: null, locationName: "", targetLocationId: null, targetLocationName: "",
                    targetWarehouseId: null, targetWarehouseName: "",
                    reference: `MVM-${Date.now()}`, description: "", date: new Date(), items: []
                });

                fetchWarehouses({ pageIndex: 1, pageSize: 5 }).then((res: any) => {
                    const warehouses = res?.data?.items || res?.items || res?.data || (Array.isArray(res) ? res : []);
                    if (warehouses?.length > 0) {
                        setFormData(prev => ({
                            ...prev,
                            sourceWarehouseId: warehouses[0].id,
                            sourceWarehouseName: warehouses[0].name
                        }));
                    }
                });
            }
        }
    }, [isOpen, initialData]);

    const addProductLine = async () => {
        const res: any = await fetchStocks({ pageIndex: 1, pageSize: 50 });
        const stockList = res?.data?.items || res?.items || res?.data || (Array.isArray(res) ? res : []);
        const selected = await stockSelect.open({
            title: "Ürün / Varyant Seç",
            data: stockList,
            columns: [
                { field: "sku", header: "Barkod" },
                { field: "name", header: "Ürün Adı" },
                { field: "quantity", header: "Mevcut Stok" }
            ],
            rowExpansionTemplate: (stock: any) => (
                <div className="p-3 bg-gray-50">
                    <DataTable value={stock.variants} className="p-datatable-sm shadow-1">
                        <Column field="name" header="Varyant" />
                        <Column field="sku" header="Barkod" />
                        <Column body={(variant) => (
                            <Button label="Seç" icon="pi pi-check" className="p-button-sm p-button-info"
                                onClick={() => stockSelect.selectItem({ ...stock, selectedVariant: variant })}
                            />
                        )} />
                    </DataTable>
                </div>
            )
        });

        if (selected) {
            const isVariant = !!selected.selectedVariant;
            const newItem = {
                id: Math.random().toString(36).substr(2, 9),
                stockId: isVariant ? selected.selectedVariant.stockId : selected.id,
                variantId: isVariant ? selected.selectedVariant.id : null,
                stockName: isVariant ? `${selected.name} (${selected.selectedVariant.name})` : selected.name,
                quantity: 0,
                rollTransactions: []
            };
            setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
        }
    };

    // --- Modal Yönetimi ---
    const openRollsDialog = (row: any) => {
        setSelectedItemForRolls(row);
        setIsRollModalOpen(true);
    };

    const handleRollsComplete = (updatedRolls?: any[]) => {
        if (updatedRolls && selectedItemForRolls) {
            const totalQuantity = updatedRolls.reduce((acc, curr) => acc + (parseFloat(curr.quantity) || 0), 0);

            setFormData(prev => ({
                ...prev,
                items: prev.items.map(i =>
                    i.id === selectedItemForRolls.id
                        ? { ...i, quantity: totalQuantity, rollTransactions: updatedRolls }
                        : i
                )
            }));
        }
        setIsRollModalOpen(false);
        setSelectedItemForRolls(null);
    };

    const handleSave = () => {
        if (formData.items.length === 0) { toast.error("En az bir ürün eklemelisiniz."); return; }
        if (!formData.sourceWarehouseId) { toast.error("Lütfen bir depo seçiniz."); return; }
        if (formData.type === 'TRANSFER' && !formData.targetWarehouseId) { toast.error("Hedef depoyu seçiniz."); return; }

        // Miktar kontrolü
        const hasZeroQuantity = formData.items.some(item => item.quantity <= 0);
        if (hasZeroQuantity) { toast.error("Miktarı 0 olan ürünler var. Lütfen top metrajlarını giriniz."); return; }

        const payload = {
            ...formData,
            id: initialData?.id
        };
        onSave(payload);
    };

    return (
        <>
            <Dialog
                visible={isOpen} onHide={onClose} maximizable maximized
                header={
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                            <Warehouse size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold leading-none">Yeni Stok Hareketi / Transfer</h2>
                            <p className="text-xs mt-1 italic text-gray-500">Stok miktarını, lokasyonunu ve topları güncelleyin</p>
                        </div>
                    </div>
                }
                footer={
                    <div className="flex justify-end gap-3 p-2">
                        <TextButton onClick={onClose} className="btn-cancel">Vazgeç</TextButton>
                        <TextButton onClick={handleSave} className="btn-new flex gap-2">
                            <Save size={20} /> <span>Hareketi Kaydet</span>
                        </TextButton>
                    </div>
                }
            >
                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 card2">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[11px] font-bold uppercase">İşlem Tipi</label>
                            <Dropdown
                                value={formData.type}
                                options={MOVEMENT_TYPES}
                                onChange={(e) => setFormData(prev => ({ ...prev, type: e.value }))}
                                className="w-full"
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <CustomSelectInput
                                label={formData.type === 'TRANSFER' ? 'Kaynak Depo' : 'Depo'}
                                value={formData.sourceWarehouseName || ""}
                                placeholder="Seçiniz..."
                                icon={Warehouse}
                                onClick={async () => {
                                    const res: any = await fetchWarehouses({ pageIndex: 1, pageSize: 10 });
                                    const warehouseList = res?.data?.items || res?.items || res?.data || (Array.isArray(res) ? res : []);
                                    const s = await warehouseSelect.open({ title: "Depo Seç", data: warehouseList, columns: [{ field: "name", header: "Depo Adı" }] });
                                    if (s) setFormData(prev => ({ ...prev, sourceWarehouseId: s.id, sourceWarehouseName: s.name, locationId: null }));
                                }}
                            />
                        </div>

                        {formData.sourceWarehouseId && (
                            <div className="flex flex-col gap-1.5">
                                <CustomSelectInput
                                    label="Kaynak Raf/Konum"
                                    value={formData.locationName || ""}
                                    placeholder="Raf Seçiniz (Opsiyonel)"
                                    icon={Map}
                                    onClick={async () => {
                                        const res: any = await fetchLocations({ pageIndex: 1, pageSize: 50 });
                                        const locationList = res?.data?.items || res?.items || res?.data || (Array.isArray(res) ? res : []);

                                        const s = await locationSelect.open({
                                            title: "Raf / Konum Seç",
                                            data: locationList,
                                            columns: [{ field: "name", header: "Konum Adı" }]
                                        });

                                        if (s) setFormData(prev => ({ ...prev, locationId: s.id, locationName: s.name }));
                                    }}
                                    onClear={formData.locationId ? () => setFormData(prev => ({ ...prev, locationId: null, locationName: "" })) : undefined}
                                />
                            </div>
                        )}

                        {formData.type === 'TRANSFER' && (
                            <div className="flex flex-col gap-1.5">
                                <CustomSelectInput
                                    label={<span className="text-emerald-600 italic">Hedef Depo</span>}
                                    value={formData.targetWarehouseName || ""}
                                    placeholder="Seçiniz..."
                                    icon={ArrowRight}
                                    onClick={async () => {
                                        const res: any = await fetchWarehouses({ pageIndex: 1, pageSize: 10 });
                                        const warehouseList = res?.data?.items || res?.items || res?.data || (Array.isArray(res) ? res : []);
                                        const s = await warehouseSelect.open({ title: "Hedef Depo Seç", data: warehouseList, columns: [{ field: "name", header: "Depo Adı" }] });
                                        if (s) setFormData(prev => ({ ...prev, targetWarehouseId: s.id, targetWarehouseName: s.name }));
                                    }}
                                />
                            </div>
                        )}

                        {formData.type === 'TRANSFER' && formData.targetWarehouseId && (
                            <div className="flex flex-col gap-1.5">
                                <CustomSelectInput
                                    label={<span className="text-emerald-600 italic">Hedef Raf/Konum</span>}
                                    value={formData.targetLocationName || ""}
                                    placeholder="Raf Seçiniz (Opsiyonel)"
                                    icon={Map}
                                    onClick={async () => {
                                        const res: any = await fetchLocations({ pageIndex: 1, pageSize: 50 });
                                        const locationList = res?.data?.items || res?.items || res?.data || (Array.isArray(res) ? res : []);

                                        const s = await locationSelect.open({
                                            title: "Hedef Raf / Konum Seç",
                                            data: locationList,
                                            columns: [{ field: "name", header: "Konum Adı" }]
                                        });

                                        if (s) setFormData(prev => ({ ...prev, targetLocationId: s.id, targetLocationName: s.name }));
                                    }}
                                    onClear={formData.targetLocationId ? () => setFormData(prev => ({ ...prev, targetLocationId: null, targetLocationName: "" })) : undefined}
                                />
                            </div>
                        )}

                        <div className="flex flex-col gap-1.5">
                            <label className="text-[11px] font-bold uppercase font-mono">Referans No</label>
                            <InputText value={formData.reference} onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))} />
                        </div>
                    </div>

                    <div className="card p-0 overflow-hidden shadow-sm">
                        <div className="p-4 flex-col sm:flex-row flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <Package className="text-blue-500" size={18} />
                                <span className="font-bold">Harekete Konu Ürünler</span>
                            </div>
                            <IconTextButton icon={Plus} onClick={addProductLine} className="btn-new btn-sm">
                                Ürün Ekle
                            </IconTextButton>
                        </div>

                        <DataTable value={formData.items} className="p-datatable-sm" responsiveLayout="scroll" emptyMessage="Henüz ürün eklemediniz.">
                            <Column header="Ürün / Varyant" body={(row) => (
                                <div className="flex flex-col">
                                    <span className="font-semibold">{row.stockName}</span>
                                    {row.variantId && <span className="text-[10px] text-blue-500 font-bold">VARYANT</span>}
                                </div>
                            )} />

                            <Column header="Miktar / Toplar" style={{ width: '300px' }} body={(row) => (
                                <div className="flex items-center gap-2">
                                    <InputNumber
                                        value={row.quantity}
                                        onValueChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                items: prev.items.map(i => i.id === row.id ? { ...i, quantity: e.value || 0 } : i)
                                            }));
                                        }}
                                        disabled={row.rollTransactions?.length > 0}
                                        className="w-32"
                                        inputClassName={`w-full text-right font-bold ${row.quantity > 0 ? 'text-green-600' : 'text-gray-400'}`}
                                    />
                                    <Button
                                        label={row.rollTransactions?.length > 0 ? `${row.rollTransactions.length} Top Girildi` : "Top Ekle"}
                                        icon="pi pi-barcode"
                                        className={`p-button-sm ${row.rollTransactions?.length > 0 ? 'p-button-success' : 'p-button-outlined'}`}
                                        onClick={() => openRollsDialog(row)}
                                    />
                                </div>
                            )} />

                            <Column style={{ width: '50px' }} body={(row) => (
                                <IconButton icon={Trash2} onClick={() => setFormData(prev => ({ ...prev, items: prev.items.filter(i => i.id !== row.id) }))} className="text-red-400 hover:text-red-600" />
                            )} />
                        </DataTable>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-bold uppercase">İşlem Açıklaması</label>
                        <InputText value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Harekete dair not ekleyin..." />
                    </div>
                </div>

                {stockSelect.modal}
                {warehouseSelect.modal}
                {locationSelect.modal}
            </Dialog>

            {/* İç Modal: Top Detayları */}
            {selectedItemForRolls && (
                <RollTransactionModal
                    isOpen={isRollModalOpen}
                    onClose={handleRollsComplete}
                    type={(formData.type === 'TRANSFER' || formData.type === 'OUT') ? 'OUT' : 'IN'}
                    initialRolls={selectedItemForRolls.rollTransactions || []}
                    stockId={selectedItemForRolls.stockId}
                    warehouseId={formData.sourceWarehouseId!}
                    variantId={selectedItemForRolls.variantId}
                />
            )}
        </>
    );
};